<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import OrdersSortMenu from './OrdersSortMenu.vue';

defineProps({
  headerTitle: { type: String, default: 'Orders' },
  searchValue: { type: String, default: '' },
  hasAppliedFilters: { type: Boolean, default: false },
  activeSort: { type: String, default: 'placed_at' },
  activeOrdering: { type: String, default: '-' },
});

const emit = defineEmits([
  'search',
  'newOrder',
  'import',
  'export',
  'toggleAdvanceFilters',
  'update:sort',
  'saveFilter',
  'openColumnConfig',
]);

const handleNewOrder = () => {
  emit('newOrder');
};

const handleImport = () => {
  emit('import');
};

const handleExport = () => {
  emit('export');
};

const handleToggleFilters = () => {
  emit('toggleAdvanceFilters');
};

const handleSaveFilter = () => {
  emit('saveFilter');
};

const handleOpenColumnConfig = () => {
  emit('openColumnConfig');
};
</script>

<template>
  <header class="sticky top-0 z-10">
    <div
      class="flex items-center justify-between w-full h-20 px-6 gap-2 mx-auto max-w-[960px]"
    >
      <span class="text-xl font-medium truncate text-n-slate-12">
        {{ headerTitle }}
      </span>
      <div class="flex items-center flex-shrink-0 gap-4">
        <div class="flex items-center gap-2">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('ORDERS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
        <div class="flex items-center gap-2">
          <div class="relative">
            <Button
              id="toggleOrdersFilterButton"
              icon="i-lucide-list-filter"
              color="slate"
              size="sm"
              class="relative w-8"
              variant="ghost"
              @click="handleToggleFilters"
            >
              <div
                v-if="hasAppliedFilters"
                class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
              />
            </Button>
            <slot name="filter" />
          </div>
          <div class="relative">
            <Button
              v-if="hasAppliedFilters"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-save"
              color="slate"
              size="sm"
              variant="ghost"
              @click="handleSaveFilter"
            />
            <div
              id="saveFilterTeleportTarget"
              class="absolute z-40 mt-2 ltr:right-0 rtl:left-0"
            />
          </div>
          <OrdersSortMenu
            :active-sort="activeSort"
            :active-ordering="activeOrdering"
            @update:sort="emit('update:sort', $event)"
          />
          <Button
            icon="i-lucide-download"
            color="slate"
            size="sm"
            variant="ghost"
            @click="handleImport"
          />
          <Button
            icon="i-lucide-upload"
            color="slate"
            size="sm"
            variant="ghost"
            @click="handleExport"
          />
          <Button
            icon="i-lucide-settings"
            color="slate"
            size="sm"
            variant="ghost"
            @click="handleOpenColumnConfig"
            :title="$t('COLUMN_CONFIG.BUTTON_TITLE')"
          />
        </div>
        <div class="w-px h-4 bg-n-strong" />
        <Button
          :label="$t('ORDERS_LAYOUT.HEADER.NEW_ORDER')"
          size="sm"
          @click="handleNewOrder"
        />
      </div>
    </div>
  </header>
</template>
